import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { getNotifications, markAllRead, markOneRead } from '@/api/notifications';
import { Button } from '@/components/ui/button/button';
import { Card } from '@/components/ui/card/card';
import type { NotificationItem } from '@/types';

import styles from './notifications.module.css';

const TYPE_META: Record<string, { icon: string; label: string }> = {
  new_job: { icon: '📘', label: '新作业' },
  graded: { icon: '✅', label: '批阅完成' },
  rejected: { icon: '↩️', label: '被打回' },
  system: { icon: '🔔', label: '系统' },
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return '刚刚';
  if (min < 60) return `${min} 分钟前`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} 天前`;
  return new Date(iso).toLocaleDateString();
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => getNotifications(50),
  });

  const readAllMutation = useMutation({
    mutationFn: () => markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const handleItemClick = async (note: NotificationItem) => {
    if (!note.is_read) {
      try {
        await markOneRead(note.id);
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      } catch {
        /* 忽略 */
      }
    }
    if (note.link) navigate(note.link);
  };

  const notes = data?.items ?? [];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>消息中心</h1>
          <p className={styles.subtitle}>
            未读 {data?.unread_count ?? 0} 条 · 新作业、批阅结果与系统通知都会在这里提醒你
          </p>
        </div>
        {(data?.unread_count ?? 0) > 0 && (
          <Button
            variant="secondary"
            loading={readAllMutation.isPending}
            onClick={() => readAllMutation.mutate()}
          >
            全部已读
          </Button>
        )}
      </header>

      {isLoading && <div className={styles.empty}>加载中...</div>}

      {!isLoading && notes.length === 0 && (
        <div className={styles.empty}>暂无消息，老师发布作业或完成批阅后会通知你。</div>
      )}

      <div className={styles.list}>
        {notes.map((note) => {
          const meta = TYPE_META[note.type] ?? TYPE_META.system;
          return (
            <Card
              key={note.id}
              className={`${styles.noteCard} ${note.is_read ? '' : styles.unread}`}
              role="button"
              tabIndex={0}
              onClick={() => handleItemClick(note)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleItemClick(note);
              }}
            >
              <span className={styles.noteIcon}>{meta.icon}</span>
              <div className={styles.noteBody}>
                <div className={styles.noteHead}>
                  <span className={styles.noteTitle}>{note.title}</span>
                  <span className={styles.noteTime}>{timeAgo(note.created_at)}</span>
                </div>
                {note.body && <p className={styles.noteBodyText}>{note.body}</p>}
                {note.link && <span className={styles.noteLink}>点击查看 →</span>}
              </div>
              {!note.is_read && <span className={styles.dot} aria-label="未读" />}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
